import { FunctionalComponent, h } from '@stencil/core';
import { FluidFunctionalComponentProps } from '../model/functional/fluid-functional-component-props.interface';
import {
  getErrorMessage,
  hasError,
} from '../utils/validation/validation.utils';
import { themeVar } from '../utils/themes/theme.variables';
import {
  applyElementOutputFormatting,
  hasValue,
  resetFocus,
} from '../utils/forms/form-method.utils';
import {
  FluidFieldValidationType,
  FluidFormElementTranslationOptions,
  FluidIconConfig,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { translateProperty } from '../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';

interface FormElementProps extends FluidFunctionalComponentProps {
  host: any;
  formLabelProps?: FormLabelProps;
  hasFocus?: boolean;
  noCancelInput?: boolean; // used in Combo Box & other components with custom controls
  onCancel?: Function;
  currentCharCount?: number;
  maxLength?: number;
  disableCharacterCount?: boolean;
  fluidTranslate?: FluidFormElementTranslationOptions;
  identifier?: string;
  adornmentConfig?: FluidIconConfig;
  enableParts?: boolean;
}

interface FormLabelProps extends FluidFunctionalComponentProps {
  host?: any;
  resetPosition?: boolean;
  inlineQuestion?: boolean;
}

/**
 * Reference to setTimeout() for "char count status" update
 */
let srCharCountTimeout;

// ================================================================ //
// -- Public Functional Components

/**
 * FormElementWrapper
 */
export const FormElementWrapper: FunctionalComponent<FormElementProps> = (
  props: FormElementProps,
  children
) => {
  const { host, formLabelProps } = props;
  const { identifier } = host;

  return (
    <div
      class={{
        [`${host.host.tagName.toLowerCase()}`]: true,
        [`theme-${host.theme}`]: true, // NEW format for theme CSS classes
        [`${host.theme}`]: true, // TODO: REMOVE once all Form Elements have been converted to SCSS
      }}
      data-test-id={`${identifier}-form-element-wrapper`}>
      <div
        class={{
          'form-group': true,
          'inline-field': host.inlineField,
        }}
        data-test-id={`${identifier}-form-group`}>
        <FormLabel
          host={host}
          resetPosition={host.readonly}
          {...formLabelProps}
        />
        <div
          class='form-element'
          data-test-id={`${identifier}-form-element-container`}>
          {children}
        </div>
        <div
          class={{
            'form-element-notifications': true,
            'inline-field': host.inlineField,
          }}>
          <Validation host={host} fluidTranslate={host.fluidTranslate} />
          <InputCharacterCount {...props} />
        </div>
      </div>
    </div>
  );
};

/**
 * FormElementWrapper
 */
export const FormElementContainer: FunctionalComponent<FormElementProps> = (
  props: FormElementProps,
  children
) => {
  const {
    host,
    hasFocus,
    noCancelInput,
    adornmentConfig,
    enableParts,
    ...rest
  } = props;
  const { identifier } = host;
  const allowAdornment = hasValue(adornmentConfig);

  return (
    <div
      {...rest}
      onMouseDown={(event) => resetFocus(host, event)}
      part={enableParts && `${host.host.tagName.toLowerCase()} form-container`}
      class={{
        'form-container': true,
        'allow-cancel': host.allowCancel,
        'allow-adornment': allowAdornment,
        'has-focus': hasFocus,
        'has-value': hasValue(host._elementState.value),
        'has-error': hasError(host._elementState, host.touched),
        disabled: host.disabled,
        'read-only': host.readonly,
        [`text-align-${host.textAlign}`]: true,
        [`${props.class}`]: !!props.class,
      }}
      data-test-id={`${identifier}-form-container`}>
      {children}
      {allowAdornment && <Adornment {...props} />}
      {!noCancelInput && <CancelInput {...props} />}
      <FocusIndicator {...props} />
    </div>
  );
};

/**
 * FocusIndicator
 */
export const FocusIndicator: FunctionalComponent<FormElementProps> = (
  props: FormElementProps
) => {
  const { host, hasFocus, enableParts } = props;
  const disabled = host.disabled || host.readonly;

  return (
    <span
      role='presentation'
      part={enableParts && `${host.host.tagName.toLowerCase()} focus-indicator`}
      class={{
        'form-field-focus-indicator': true,
        'has-focus': !!hasFocus && !disabled,
      }}
    />
  );
};

/**
 * FormLabel
 */
export const FormLabel: FunctionalComponent<FormLabelProps> = (
  props: FormLabelProps
) => {
  const { host, resetPosition, inlineQuestion } = props;

  if (!host.label && !host.fluidTranslate?.label) return;

  return (
    <fluid-form-label
      overrideGlobalTheme={host.overrideGlobalTheme}
      theme={host.theme}
      inlineQuestion={inlineQuestion}
      disabled={host.disabled}
      elementState={host._elementState}
      supplementaryLabel={host.supplementaryLabel}
      validation={host.validation}
      touched={host.touched}
      label={translateProperty.bind(host)('label')}
      textOverflow={host.labelTextOverflow}
      helpText={translateProperty.bind(host)('helpText')}
      resetPosition={resetPosition}
      class={!!props.class && `${props.class}`}
    />
  );
};

/**
 * CancelInput
 */
export const CancelInput: FunctionalComponent<FormElementProps> = (
  props: FormElementProps
) => {
  const { host, onCancel, enableParts } = props;
  const preCancelValue = host._elementState.value;
  const resetAndValidate = () => {
    host.reset(true);
    host._elementRef?.focus();
    host.validate().then(() => {
      host.valueChange.emit({
        ...applyElementOutputFormatting(host),
        cleared: true,
      });
      if (onCancel) {
        onCancel(preCancelValue);
      }
    });
  };

  if (showCancelInput(host)) {
    return (
      <button
        class='cancel'
        onClick={() => resetAndValidate()}
        title='Clear Field'
        part={
          enableParts && `${host.host.tagName.toLowerCase()} cancel-button`
        }>
        <fluid-icon
          key={`${host.host.tagName.toLowerCase()}-cancel-button`}
          config={{
            theme: host.theme,
            overrideGlobalTheme: true,
            ...themeVar<FluidIconConfig>(
              host.theme,
              'icons.form_elements.cancel'
            ),
          }}
        />
      </button>
    );
  }
};

/**
 * Adornment icon
 */
export const Adornment: FunctionalComponent<FormElementProps> = (
  props: FormElementProps
) => {
  const { host, adornmentConfig, enableParts } = props;

  return (
    <div
      class='adornment'
      part={enableParts && `${host.host.tagName.toLowerCase()} adornment`}>
      <fluid-icon
        config={{
          ...adornmentConfig,
          theme: host.theme,
          overrideGlobalTheme: true,
        }}
      />
    </div>
  );
};

/**
 * Validation
 */
export const Validation: FunctionalComponent<FormElementProps> = (
  props: FormElementProps
) => {
  const { host } = props;
  const {
    _elementState,
    externalState,
    parentState,
    _i18nStore,
    fluidTranslate,
    touched,
  } = host;
  const visible = hasError(_elementState, touched);
  const errorMessage = getErrorMessage(
    _elementState,
    fluidTranslate,
    _i18nStore,
    parentState,
    externalState
  );

  if (!visible) return;

  return <ValidationMessage visible={visible} errorMessage={errorMessage} />;
};

export const ValidationMessage = (props: {
  visible: boolean;
  errorMessage: string;
}) => {
  const { visible, errorMessage } = props;
  return (
    <div
      class={{
        'input-validation error': true,
        'error-visible': visible,
        'error-hidden': !visible,
      }}>
      <span title={errorMessage} class='error-message'>
        {errorMessage}
      </span>
    </div>
  );
};
/**
 * Character Counter for Text Area / Input Field
 */
export const InputCharacterCount: FunctionalComponent<FormElementProps> = (
  props: FormElementProps
) => {
  const { maxLength, disableCharacterCount, currentCharCount } = props;
  let srStatusRef: HTMLElement;

  if (isNullOrUndefined(maxLength) || disableCharacterCount) return;

  // Debounce screen reader "char count status" (prevent update every keystroke)
  clearTimeout(srCharCountTimeout);

  // Delay screen reader "char count status" update by 1 sec to allow user input to be read first
  srCharCountTimeout = setTimeout(() => {
    const remainingCharCount = maxLength - (currentCharCount || 0);
    if (remainingCharCount === parseInt(srStatusRef?.textContent, 10)) return;
    !!srStatusRef &&
      (srStatusRef.innerHTML = `${remainingCharCount} characters remaining`);
  }, 1000);

  return (
    <div class='input-character-count'>
      <span id='sr-char-count' class='sr-only'>
        Enter up to {maxLength} characters
      </span>
      <div class='char-count-status' aria-hidden='true'>
        <span>{currentCharCount || 0}</span>
        <span>/</span>
        <span>{maxLength}</span>
      </div>
      <span
        ref={(el: HTMLElement) => (srStatusRef = el)}
        class='sr-only'
        aria-live='polite'
      />
    </div>
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

/**
 * Determines if CancelInput should be displayed within a form field
 * component. Returns true if form field does NOT have "required"
 * validation, allows CancelInput functionality, is NOT disabled, is
 * NOT read-only, and has a user-inputted value.
 * @param host
 */
const showCancelInput = (host): boolean => {
  const isRequired = !!(host.validation || []).find(
    (v) => v.type === FluidFieldValidationType.REQUIRED
  );
  return (
    !isRequired &&
    host.allowCancel &&
    !host.disabled &&
    !host.readonly &&
    hasValue(host._elementState.value)
  );
};
